import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { centroid, normalizePoint, useZones } from 'features/Responder/features/Zones';
import React from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { PiCrosshair, PiX } from 'react-icons/pi';
import PlacesAutocompleteInput from './PlacesAutocompleteInput.jsx';

const StartLocationCard = ({
  startLocation,
  googleApiKey,
  usingCurrentStart,
  setUsingCurrentStart,
  setStartLocation,
  setAutoBuildPending,
  setSelectedCheckpointId = () => {},
  cardId,
}) => {
  let zonesWithMeta = [];
  try {
    const zonesCtx = useZones();
    zonesWithMeta = zonesCtx?.zonesWithMeta || zonesCtx?.zones || [];
  } catch (err) {
    zonesWithMeta = [];
  }

  const selectedZoneId = startLocation?.zoneId ?? startLocation?.geometry?.zoneId ?? '';
  const selectedZoneValue =
    selectedZoneId === null || selectedZoneId === undefined ? '' : `${selectedZoneId}`;

  const isZoneSelected = selectedZoneValue !== '';

  const zoneOptions = zonesWithMeta.map((z) => ({
    value: `${z.id}`,
    label: z.name || `Zone ${z.id}`,
  }));

  const handleClearZone = () => {
    if (!isZoneSelected) return;
    setStartLocation((prev) => ({
      ...prev,
      address: '',
      lat: '',
      lng: '',
      placeId: '',
      useCurrent: false,
      zoneId: null,
      geometry: null,
    }));
    setUsingCurrentStart(false);
    setAutoBuildPending(true);
  };

  const handleZoneChange = (val) => {
    if (!val) {
      setStartLocation((prev) => ({
        ...prev,
        zoneId: null,
        geometry: null,
      }));
      setUsingCurrentStart(false);
      setAutoBuildPending(true);
      return;
    }

    const sel = zonesWithMeta.find((z) => `${z.id}` === `${val}`);
    if (!sel) return;

    const raw = sel.polygon ?? sel.geometry?.points ?? sel.geometry?.coord ?? sel._points ?? [];
    const points = Array.isArray(raw) ? raw.map((pt) => normalizePoint(pt)).filter(Boolean) : [];
    const newGeometry = {
      type: 'zone',
      points,
      coord: points,
      isZone: true,
      zoneId: sel.id,
    };
    const zoneLabel = sel.name || `Zone ${sel.id}`;
    const boundsCenter =
      sel?.bounds &&
      Number.isFinite(sel.bounds.north) &&
      Number.isFinite(sel.bounds.south) &&
      Number.isFinite(sel.bounds.east) &&
      Number.isFinite(sel.bounds.west)
        ? {
            lat: (sel.bounds.north + sel.bounds.south) / 2,
            lng: (sel.bounds.east + sel.bounds.west) / 2,
          }
        : null;
    const center = centroid(points) || boundsCenter || points[0] || null;

    setUsingCurrentStart(false);
    setStartLocation((prev) => ({
      ...prev,
      geometry: newGeometry,
      zoneId: sel.id,
      label: zoneLabel || prev.label,
      address: zoneLabel || prev.address,
      lat: center?.lat ?? prev.lat,
      lng: center?.lng ?? prev.lng,
      placeId: '',
      useCurrent: false,
    }));
    setSelectedCheckpointId('start');
    setAutoBuildPending(true);
  };

  return (
    <Card id={cardId} className="shadow-sm bg-primary-shadow text-theme border-theme-strong">
      <Card.Body className="p-3">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div>
            <div className="fw-semibold">Start</div>
            <div className="text-theme-muted small">
              Use current location or enter a start point.
            </div>
          </div>
          <Button
            variant={usingCurrentStart ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              if (!navigator.geolocation) return;
              setUsingCurrentStart(true);
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  const lat = position.coords.latitude;
                  const lng = position.coords.longitude;
                  setStartLocation((prev) => ({
                    ...prev,
                    lat,
                    lng,
                    placeId: '',
                    address: 'Current location',
                    useCurrent: true,
                    zoneId: null,
                    geometry: null,
                  }));
                  setSelectedCheckpointId('start');
                  setAutoBuildPending(true);
                },
                () => setUsingCurrentStart(false),
              );
            }}
            title="Use current location"
          >
            <PiCrosshair size={24} />
          </Button>
        </div>

        <Form.Group className="mt-2">
          <Form.Label className="small text-theme-muted mb-1">Address</Form.Label>
          <PlacesAutocompleteInput
            stop={startLocation}
            placeholder="Search address or place"
            apiKey={googleApiKey}
            onManualChange={(val) =>
              setStartLocation((prev) => ({
                ...prev,
                address: val,
                useCurrent: false,
              }))
            }
            onPlaceSelect={(payload) => {
              setStartLocation((prev) => ({
                ...prev,
                address: payload.address || prev.address,
                lat: payload.lat ?? prev.lat,
                lng: payload.lng ?? prev.lng,
                placeId: payload.placeId || prev.placeId,
                useCurrent: false,
                zoneId: null,
                geometry: null,
              }));
              setSelectedCheckpointId('start');
              setAutoBuildPending(true);
            }}
          />
        </Form.Group>

        <Form.Group className="mt-2">
          <Form.Label className="small text-theme-muted mb-1">Zone</Form.Label>
          <div className="d-flex align-items-stretch gap-2">
            <BaseSelect
              wrapperClassName="flex-fill"
              isSearchable
              classNames={{ control: () => 'responder-input' }}
              selectProps={{
                'aria-label': 'Zone',
                options: zoneOptions,
                value: findSelectOption(zoneOptions, selectedZoneValue),
                placeholder: '-- Select zone --',
                onChange: (option) => handleZoneChange(option?.value ?? ''),
              }}
            />
            <Button
              variant="primary"
              onClick={handleClearZone}
              disabled={!isZoneSelected}
              title="Clear zone"
              aria-label="Clear zone"
            >
              <PiX size={16} />
            </Button>
          </div>
        </Form.Group>

        <Row className="mt-2 g-2">
          <Col>
            <Form.Label className="small text-theme-muted mb-1">Latitude</Form.Label>
            <Form.Control
              className="border-0 responder-input"
              size="sm"
              value={startLocation.lat}
              readOnly={isZoneSelected}
              onChange={(e) =>
                setStartLocation((prev) => ({
                  ...prev,
                  lat: e.target.value,
                  useCurrent: false,
                  zoneId: null,
                  geometry: null,
                }))
              }
              placeholder="e.g. 30.2672"
            />
          </Col>
          <Col>
            <Form.Label className="small text-theme-muted mb-1">Longitude</Form.Label>
            <Form.Control
              className="border-0 responder-input"
              size="sm"
              value={startLocation.lng}
              readOnly={isZoneSelected}
              onChange={(e) =>
                setStartLocation((prev) => ({
                  ...prev,
                  lng: e.target.value,
                  useCurrent: false,
                  zoneId: null,
                  geometry: null,
                }))
              }
              placeholder="-97.7431"
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default StartLocationCard;
